/**
 * Per-character fill inside ONE text element.
 *
 * A heading is often two colours — "Enhance your music **Experience**" — and
 * splitting that into two elements breaks wrapping, alignment and every later
 * edit. So a text element may carry `colorSpans`: ranges of character offsets
 * into its `content` that paint in their own fill, over the element's own
 * `styles.color` (or its gradient) everywhere else.
 *
 * A span's fill is a solid `color`, and OPTIONALLY a `fill` gradient
 * ({type: 'linear'|'radial', color1, color2, angle, centerX, centerY}) painted
 * across the run. `color` is kept even on a gradient span as the flat fallback,
 * so a consumer that can only take one colour (an old build reading a saved
 * document) still paints something sensible.
 *
 * Offsets, not a run array, on purpose: `content` stays a plain string, so
 * measuring, wrapping, the Bijoy repair, placeholders and the whole export path
 * keep working on the text exactly as before, and an element with no spans is
 * byte-for-byte the document it always was.
 *
 * Spans are kept normalised — sorted, non-overlapping, clamped to the text, with
 * touching same-fill ranges merged — so every consumer can walk them in order.
 */

/** The gradient half of a span's fill, or null when the span is a plain colour. */
function normalizeFill(fill) {
    const type = fill?.type;
    if (type !== 'linear' && type !== 'radial') return null;
    const out = { type };
    if (typeof fill.color1 === 'string') out.color1 = fill.color1;
    if (typeof fill.color2 === 'string') out.color2 = fill.color2;
    if (fill.angle != null && Number.isFinite(Number(fill.angle))) out.angle = Number(fill.angle);
    if (fill.centerX != null && Number.isFinite(Number(fill.centerX))) out.centerX = Number(fill.centerX);
    if (fill.centerY != null && Number.isFinite(Number(fill.centerY))) out.centerY = Number(fill.centerY);
    return out;
}

/**
 * Identity of a paint, so that neighbouring runs painted the same way merge into
 * one span instead of fragmenting the list on every edit.
 */
function paintKey(paint) {
    if (!paint) return '';
    // The colour-preset token is part of a paint's identity: two runs that look
    // the same but belong to different global colours (utils/colorUsage) must not
    // merge, or one of the two globals silently loses its characters.
    const t = paint.token ? `#${paint.token}` : '';
    const f = paint.fill;
    if (!f) return `s|${paint.color}${t}`;
    return `g|${f.type}|${f.color1}|${f.color2}|${f.angle}|${f.centerX}|${f.centerY}|${paint.color}${t}`;
}

/** {color, fill, token} as it is stored on a span, or null for "no own fill". */
export function normalizePaint(paint) {
    if (!paint) return null;
    const fill = normalizeFill(paint.fill || (paint.type ? paint : null));
    const color = typeof paint.color === 'string' ? paint.color : null;
    // Carried through every normalisation so a span keeps its global colour
    // across text edits, which is the only place span identity can live.
    const token = typeof paint.token === 'string' && paint.token ? { token: paint.token } : null;
    if (fill) return { color: color || fill.color1 || '#000000', fill, ...token };
    return color ? { color, ...token } : null;
}

/** Sorted, clamped, non-overlapping, merged. Later spans win over earlier ones. */
export function normalizeColorSpans(spans, length) {
    const list = (Array.isArray(spans) ? spans : [])
        .map((s) => ({
            start: Math.max(0, Math.min(length, Math.floor(Number(s?.start) || 0))),
            end: Math.max(0, Math.min(length, Math.floor(Number(s?.end) || 0))),
            paint: normalizePaint(s),
        }))
        .filter((s) => s.paint && s.end > s.start);
    if (!list.length) return [];

    // Paint them onto a per-character map in order, which resolves overlap by
    // "last one wins" without any interval arithmetic.
    const paint = new Array(length).fill(null);
    list.forEach((s) => {
        for (let i = s.start; i < s.end; i += 1) paint[i] = s.paint;
    });

    const out = [];
    let i = 0;
    while (i < length) {
        const cur = paint[i];
        if (!cur) { i += 1; continue; }
        const key = paintKey(cur);
        let j = i + 1;
        while (j < length && paint[j] && paintKey(paint[j]) === key) j += 1;
        out.push({ start: i, end: j, ...cur });
        i = j;
    }
    return out;
}

/**
 * Paint the characters in [start, end) with one fill — a `{color, fill}` paint,
 * or null to hand them back to the element's own fill.
 */
export function applyFillToRange(spans, start, end, paint, length) {
    const from = Math.max(0, Math.min(length, Math.floor(start)));
    const to = Math.max(0, Math.min(length, Math.floor(end)));
    if (to <= from) return normalizeColorSpans(spans, length);
    const next = normalizePaint(paint);

    const kept = [];
    normalizeColorSpans(spans, length).forEach((s) => {
        // Whatever the range does not cover survives, split around it if needed.
        if (s.end <= from || s.start >= to) { kept.push(s); return; }
        if (s.start < from) kept.push({ ...s, start: s.start, end: from });
        if (s.end > to) kept.push({ ...s, start: to, end: s.end });
    });
    if (next) kept.push({ start: from, end: to, ...next });
    return normalizeColorSpans(kept, length);
}

/** Where each word begins — the only places a colour change reads as deliberate. */
function wordStarts(text) {
    const out = [];
    for (let i = 1; i < text.length; i += 1) {
        if (/\s/.test(text[i - 1]) && !/\s/.test(text[i])) out.push(i);
    }
    return out;
}

/** The paint covering most of a line of `length`, or null when most of it is bare. */
function dominantPaint(list, length) {
    let uncovered = length;
    let best = null;
    let bestWidth = 0;
    list.forEach((s) => {
        const width = s.end - s.start;
        uncovered -= width;
        if (width > bestWidth) { bestWidth = width; best = normalizePaint(s); }
    });
    return bestWidth > uncovered ? best : null;
}

/** The word start nearest `index`, or `index` itself when the line is one word. */
function snapToWord(index, starts) {
    if (!starts.length) return index;
    return starts.reduce(
        (best, s) => (Math.abs(s - index) < Math.abs(best - index) ? s : best),
        starts[0],
    );
}

/**
 * The same runs laid over a different text, each keeping its share of the line.
 *
 * Used when an edit leaves nothing to anchor to — the whole text replaced, or
 * the box emptied and typed into again. A two-colour heading retyped from
 * scratch comes back two-coloured in the same proportions instead of flattening
 * into whichever colour the caret happened to sit on.
 *
 * Proportions alone would cut "Jane Doe" at "Jane D|oe", which nobody ever means,
 * so a boundary lands on the nearest word start. Given the whole new text this
 * snapping happens; given only a length it cannot, and the split stays where the
 * arithmetic put it.
 */
export function rescaleColorSpans(spans, oldLength, newText) {
    const text = typeof newText === 'string' ? newText : null;
    const from = Math.max(0, Math.floor(Number(oldLength) || 0));
    const to = text ? text.length : Math.max(0, Math.floor(Number(newText) || 0));
    const list = normalizeColorSpans(spans, from);
    if (!list.length || !from || !to) return [];

    const scale = to / from;
    const starts = text ? wordStarts(text) : [];

    // One word has nowhere to change colour that isn't the middle of a word, so it
    // takes whichever paint owned most of the old line — including none of it,
    // when most of that line was the element's own fill.
    if (text && !starts.length) {
        const paint = dominantPaint(list, from);
        return paint ? normalizeColorSpans([{ start: 0, end: to, ...paint }], to) : [];
    }
    const at = (offset) => {
        const raw = Math.round(offset * scale);
        return raw <= 0 || raw >= to ? raw : snapToWord(raw, starts);
    };

    // Walked in order with `prev` as the floor: two runs that snap to the same
    // word must not swallow one another — every run keeps at least one character.
    let prev = 0;
    const moved = list.map((s) => {
        const start = Math.max(prev, at(s.start));
        const end = Math.max(start + 1, at(s.end));
        prev = end;
        return { ...s, start, end };
    });
    return normalizeColorSpans(moved, to);
}

/**
 * Did the edit leave NOTHING of the old text standing — select-all-and-retype,
 * or the box emptied? There is then no caret to inherit a colour from, which is
 * what makes such an edit different from every other one.
 */
export function replacedWholeText(oldText, newText) {
    const before = String(oldText ?? '');
    const after = String(newText ?? '');
    if (!before.length) return false;
    if (!after.length) return true;
    return before[0] !== after[0]
        && before[before.length - 1] !== after[after.length - 1];
}

/**
 * Move spans across a text edit.
 *
 * The editor hands us the before and after strings, not a diff, so the change is
 * localised by the longest common prefix and suffix — which is exactly right for
 * how text is actually edited (typing, deleting, pasting over a selection).
 * Characters before the change keep their fill, characters after it shift, and
 * the newly typed characters take the fill of whatever they replaced, so typing
 * inside a coloured word stays that colour.
 */
export function remapColorSpans(spans, oldText, newText) {
    const before = String(oldText ?? '');
    const after = String(newText ?? '');
    const list = normalizeColorSpans(spans, before.length);
    if (!list.length) return [];
    if (before === after) return list;

    let prefix = 0;
    const maxPrefix = Math.min(before.length, after.length);
    while (prefix < maxPrefix && before[prefix] === after[prefix]) prefix += 1;

    let suffix = 0;
    const maxSuffix = Math.min(before.length - prefix, after.length - prefix);
    while (
        suffix < maxSuffix
        && before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
    ) suffix += 1;

    const removedFrom = prefix;
    const removedTo = before.length - suffix;
    const insertedLength = after.length - suffix - prefix;
    const delta = insertedLength - (removedTo - removedFrom);

    // Nothing of the old text survived — select-all-and-retype. There is no
    // caret to inherit from, so keep the run LAYOUT rather than one run's colour.
    if (removedFrom === 0 && removedTo === before.length && insertedLength > 0) {
        return rescaleColorSpans(list, before.length, after.length);
    }

    // The fill the inserted characters inherit: whatever was immediately before
    // the caret, else whatever the replaced text was.
    const paintAt = (index) => {
        const hit = list.find((s) => index >= s.start && index < s.end);
        return hit ? normalizePaint(hit) : null;
    };
    const insertPaint = insertedLength > 0
        ? (paintAt(removedFrom - 1) || paintAt(removedFrom))
        : null;

    const moved = [];
    list.forEach((s) => {
        const start = s.start <= removedFrom ? s.start
            : s.start >= removedTo ? s.start + delta
                : removedFrom;
        const end = s.end <= removedFrom ? s.end
            : s.end >= removedTo ? s.end + delta
                : removedFrom;
        if (end > start) moved.push({ ...s, start, end });
    });
    if (insertPaint) {
        moved.push({ start: removedFrom, end: removedFrom + insertedLength, ...insertPaint });
    }
    return normalizeColorSpans(moved, after.length);
}

/**
 * Split `text[start..end)` into consecutive pieces of one fill each.
 * `color: null` (and no `fill`) means "use the element's own fill" — the caller
 * decides whether that is a solid colour or a gradient.
 */
export function paintSegments(spans, text, start = 0, end = null, length = null) {
    const str = String(text ?? '');
    const len = length == null ? str.length : length;
    const from = Math.max(0, Math.min(len, start));
    const to = Math.max(from, Math.min(len, end == null ? len : end));
    const list = normalizeColorSpans(spans, len).filter((s) => s.end > from && s.start < to);
    if (!list.length) {
        return to > from ? [{ text: str.slice(from, to), color: null, fill: null, start: from }] : [];
    }

    const out = [];
    let cursor = from;
    list.forEach((s) => {
        const sStart = Math.max(from, s.start);
        const sEnd = Math.min(to, s.end);
        if (sStart > cursor) {
            out.push({ text: str.slice(cursor, sStart), color: null, fill: null, start: cursor });
        }
        out.push({
            text: str.slice(sStart, sEnd), color: s.color, fill: s.fill || null, start: sStart,
        });
        cursor = sEnd;
    });
    if (cursor < to) out.push({ text: str.slice(cursor, to), color: null, fill: null, start: cursor });
    return out.filter((seg) => seg.text !== '');
}

/** The paint at one character offset, or null for "the element's own fill". */
export function paintAtIndex(spans, index) {
    if (!Array.isArray(spans)) return null;
    const hit = spans.find((s) => index >= s.start && index < s.end);
    return hit ? normalizePaint(hit) : null;
}

/** Does this element paint any of its characters separately? */
export function hasColorSpans(el) {
    return Array.isArray(el?.colorSpans)
        && el.colorSpans.some((s) => normalizePaint(s) && s.end > s.start);
}

/** The element's spans, clamped to the text it actually holds now. */
export function elementColorSpans(el, text) {
    if (!hasColorSpans(el)) return [];
    return normalizeColorSpans(el.colorSpans, String(text ?? '').length);
}
